import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import type { ChecklistTemplate } from "@/types/components";

const apiUrl = import.meta.env.VITE_CLOUDFLARE_API_URL || "http://localhost:8788";

interface UsePricingConfiguratorMutationsProps {
  templateId?: number | null;
  template: ChecklistTemplate | null;
}

export function usePricingConfiguratorMutations({
  templateId,
  template,
}: UsePricingConfiguratorMutationsProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Save pricing configuration mutation
  const savePricingMutation = useMutation({
    mutationFn: async (pricingData: any) => {
      if (!templateId) throw new Error("Template ID required");

      const response = await fetch(`${apiUrl}/api/v1/templates/${templateId}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...template,
          pricing_data: pricingData,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to save pricing configuration");
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["templates"] });
      toast({
        title: "Pricing Saved",
        description: "Pricing configuration has been saved successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error.message || "Failed to save pricing configuration",
        variant: "destructive",
      });
    },
  });

  return {
    savePricingMutation,
    isSaving: savePricingMutation.isPending,
  };
}
